import { useRef, useState, type ReactNode } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { useClickOutside } from '../lib/useClickOutside';

export interface MenuOption<V extends string> {
  value: V;
  label: string;
}

/** Top-bar select: a button showing the current value, opening a checkable list. */
export function SelectMenu<V extends string>({
  icon,
  prefix,
  value,
  options,
  onChange,
  label,
}: {
  icon?: ReactNode;
  prefix?: string;
  value: V;
  options: MenuOption<V>[];
  onChange: (v: V) => void;
  label: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(ref, open, () => setOpen(false));
  const current = options.find((o) => o.value === value)?.label ?? value;
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        className="ctl"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`${label}: ${current}`}
        onClick={() => setOpen((o) => !o)}
      >
        {icon}
        {prefix && <span className="muted">{prefix}</span>}
        <span style={{ fontWeight: 600 }}>{current}</span>
        <ChevronsUpDown size={13} />
      </button>
      {open && (
        <div className="popover" role="menu" style={{ top: 36, left: 0 }}>
          {options.map((o) => (
            <button
              key={o.value}
              type="button"
              role="menuitemradio"
              aria-checked={o.value === value}
              className="menu-item"
              onClick={() => {
                onChange(o.value);
                setOpen(false);
              }}
            >
              {o.label}
              {o.value === value && <Check size={14} className="check" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Multi-select for channel families; empty selection = all. */
export function MultiMenu({
  icon,
  value,
  options,
  onChange,
  allLabel,
  label,
}: {
  icon?: ReactNode;
  value: string[];
  options: MenuOption<string>[];
  onChange: (v: string[]) => void;
  allLabel: string;
  label: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(ref, open, () => setOpen(false));
  const text = !value.length
    ? allLabel
    : value.length === 1
      ? (options.find((o) => o.value === value[0])?.label ?? value[0])
      : `${value.length} channel families`;
  const toggle = (v: string) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        className="ctl"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`${label}: ${text}`}
        onClick={() => setOpen((o) => !o)}
      >
        {icon}
        <span style={{ fontWeight: 600 }}>{text}</span>
        <ChevronsUpDown size={13} />
      </button>
      {open && (
        <div className="popover" role="menu" style={{ top: 36, right: 0 }}>
          <button
            type="button"
            role="menuitemcheckbox"
            aria-checked={!value.length}
            className="menu-item"
            onClick={() => onChange([])}
          >
            {allLabel}
            {!value.length && <Check size={14} className="check" />}
          </button>
          <div className="menu-sep" />
          {options.map((o) => (
            <button
              key={o.value}
              type="button"
              role="menuitemcheckbox"
              aria-checked={value.includes(o.value)}
              className="menu-item"
              onClick={() => toggle(o.value)}
            >
              {o.label}
              {value.includes(o.value) && <Check size={14} className="check" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
